import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Trash2, Loader2, Home, Edit, Building2, Users, Layers } from 'lucide-react';
import { toast } from 'sonner';

export default function UnitsPage() {
  const [units, setUnits] = useState([]);
  const [communityInfo, setCommunityInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  // Single unit form
  const [unitNumber, setUnitNumber] = useState('');
  const [block, setBlock] = useState('');
  const [floor, setFloor] = useState('');
  const [unitType, setUnitType] = useState('residential');
  const [areaSqft, setAreaSqft] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [residentName, setResidentName] = useState('');
  const [residentEmail, setResidentEmail] = useState('');
  const [residentPhone, setResidentPhone] = useState('');
  const [status, setStatus] = useState('vacant');

  // Bulk creation form
  const [bulkPattern, setBulkPattern] = useState('');
  const [bulkBlock, setBulkBlock] = useState('');
  const [bulkType, setBulkType] = useState('residential');
  const [bulkArea, setBulkArea] = useState('');
  const [bulkBedrooms, setBulkBedrooms] = useState('');
  const [bulkPreview, setBulkPreview] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [unitsRes, infoRes] = await Promise.all([
        api.get('/admin/units'),
        api.get('/admin/community-info')
      ]);
      setUnits(unitsRes.data);
      setCommunityInfo(infoRes.data);
    } catch { toast.error('Failed to load units'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setUnitNumber(''); setBlock(''); setFloor(''); setUnitType('residential');
    setAreaSqft(''); setBedrooms(''); setResidentName(''); setResidentEmail('');
    setResidentPhone(''); setStatus('vacant');
  };

  const createUnit = async () => {
    if (!unitNumber) { toast.error('Unit number is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/units', {
        unit_number: unitNumber, block, floor, unit_type: unitType,
        area_sqft: areaSqft ? parseFloat(areaSqft) : null,
        bedrooms: bedrooms ? parseInt(bedrooms) : null,
        resident_name: residentName, resident_email: residentEmail,
        resident_phone: residentPhone, status
      });
      toast.success('Unit created');
      setShowNew(false);
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create unit'); }
    finally { setSubmitting(false); }
  };

  const updateUnit = async () => {
    if (!editUnit) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/units/${editUnit.unit_id}`, {
        unit_number: unitNumber, block, floor, unit_type: unitType,
        area_sqft: areaSqft ? parseFloat(areaSqft) : null,
        bedrooms: bedrooms ? parseInt(bedrooms) : null,
        resident_name: residentName, resident_email: residentEmail,
        resident_phone: residentPhone, status
      });
      toast.success('Unit updated');
      setEditUnit(null);
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSubmitting(false); }
  };

  const deleteUnit = async (unitId) => {
    if (!confirm('Delete this unit?')) return;
    try { await api.delete(`/admin/units/${unitId}`); toast.success('Unit deleted'); loadData(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEdit = (unit) => {
    setUnitNumber(unit.unit_number || '');
    setBlock(unit.block || '');
    setFloor(unit.floor || '');
    setUnitType(unit.unit_type || 'residential');
    setAreaSqft(unit.area_sqft ? String(unit.area_sqft) : '');
    setBedrooms(unit.bedrooms ? String(unit.bedrooms) : '');
    setResidentName(unit.resident_name || '');
    setResidentEmail(unit.resident_email || '');
    setResidentPhone(unit.resident_phone || '');
    setStatus(unit.status || 'vacant');
    setEditUnit(unit);
  };

  // Parse bulk pattern like "101-104, 201-204, 301-304"
  const parseBulkPattern = (pattern) => {
    const units = [];
    const ranges = pattern.split(',').map(s => s.trim()).filter(Boolean);
    
    for (const range of ranges) {
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(s => s.trim());
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        
        if (!isNaN(startNum) && !isNaN(endNum) && startNum <= endNum) {
          // Extract prefix (letters before numbers)
          const prefix = start.replace(/[0-9]/g, '');
          const startDigits = start.replace(/[^0-9]/g, '');
          const padLength = startDigits.length;
          
          for (let i = startNum; i <= endNum; i++) {
            const numStr = String(i).padStart(padLength, '0');
            units.push(prefix + numStr);
          }
        }
      } else {
        units.push(range);
      }
    }
    return units;
  };

  const updateBulkPreview = (pattern) => {
    setBulkPattern(pattern);
    const parsed = parseBulkPattern(pattern);
    setBulkPreview(parsed.slice(0, 20)); // Show max 20 in preview
  };

  const createBulkUnits = async () => {
    const unitNumbers = parseBulkPattern(bulkPattern);
    if (unitNumbers.length === 0) { toast.error('No valid unit numbers'); return; }
    
    setSubmitting(true);
    let created = 0;
    let failed = 0;
    
    for (const num of unitNumbers) {
      try {
        // Extract floor from unit number (first digit(s))
        const floorMatch = num.match(/^[A-Za-z]*(\d)/);
        const autoFloor = floorMatch ? floorMatch[1] : '';
        
        await api.post('/admin/units', {
          unit_number: bulkBlock ? `${bulkBlock}-${num}` : num,
          block: bulkBlock,
          floor: autoFloor,
          unit_type: bulkType,
          area_sqft: bulkArea ? parseFloat(bulkArea) : null,
          bedrooms: bulkBedrooms ? parseInt(bulkBedrooms) : null,
          status: 'vacant'
        });
        created++;
      } catch {
        failed++;
      }
    }
    
    setSubmitting(false);
    toast.success(`Created ${created} units${failed > 0 ? `, ${failed} failed (may already exist)` : ''}`);
    setShowBulk(false);
    setBulkPattern('');
    setBulkPreview([]);
    loadData();
  };

  const filtered = units.filter(u => {
    const matchSearch = u.unit_number?.toLowerCase().includes(search.toLowerCase()) ||
      u.block?.toLowerCase().includes(search.toLowerCase()) ||
      u.resident_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalUnits = communityInfo?.total_units || 0;
  const registeredUnits = communityInfo?.unit_count || units.length;
  const occupiedUnits = units.filter(u => u.status === 'occupied').length;
  const vacantUnits = units.filter(u => u.status === 'vacant').length;
  const progressPct = totalUnits > 0 ? Math.round((registeredUnits / totalUnits) * 100) : 0;

  const statusBadge = (s) => ({
    occupied: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    vacant: 'bg-slate-100 text-slate-600 border-slate-200',
    maintenance: 'bg-orange-50 text-orange-700 border-orange-100',
    reserved: 'bg-blue-50 text-blue-700 border-blue-100'
  }[s] || 'bg-slate-100 text-slate-600');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="units-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Units Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            {communityInfo?.community?.name || 'Community'} — {registeredUnits} of {totalUnits} units registered
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1" onClick={() => setShowBulk(true)} data-testid="bulk-add-btn">
            <Layers size={14} /> Bulk Add
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => { resetForm(); setShowNew(true); }} data-testid="add-unit-btn">
            <Plus size={14} /> Add Unit
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 size={18} className="text-blue-600" /></div>
            <div><p className="text-xs text-slate-500">Total Units</p><p className="text-lg font-bold text-slate-900">{totalUnits}</p></div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Home size={18} className="text-emerald-600" /></div>
            <div><p className="text-xs text-slate-500">Occupied</p><p className="text-lg font-bold text-emerald-700">{occupiedUnits}</p></div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Home size={18} className="text-slate-500" /></div>
            <div><p className="text-xs text-slate-500">Vacant</p><p className="text-lg font-bold text-slate-700">{vacantUnits}</p></div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center"><Users size={18} className="text-orange-600" /></div>
            <div><p className="text-xs text-slate-500">Registered</p><p className="text-lg font-bold text-orange-700">{registeredUnits}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      {totalUnits > 0 && (
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-700">Registration Progress</p>
              <p className="text-xs text-slate-500">{registeredUnits} / {totalUnits} ({progressPct}%)</p>
            </div>
            <Progress value={progressPct} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by unit, block or resident..." className="pl-9 text-sm" data-testid="unit-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] text-sm" data-testid="unit-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="occupied">Occupied</SelectItem>
            <SelectItem value="vacant">Vacant</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Units Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Unit</TableHead>
                <TableHead className="text-xs">Block / Floor</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Area</TableHead>
                <TableHead className="text-xs">Resident</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-8">
                  {units.length === 0 ? 'No units added yet. Click "Add Unit" or "Bulk Add" to get started.' : 'No units match your filter.'}
                </TableCell></TableRow>
              )}
              {filtered.map(u => (
                <TableRow key={u.unit_id} data-testid={`unit-row-${u.unit_id}`}>
                  <TableCell className="font-medium text-sm">{u.unit_number}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {[u.block, u.floor ? `Floor ${u.floor}` : ''].filter(Boolean).join(' / ') || '-'}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs capitalize">{u.unit_type}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {u.area_sqft ? `${u.area_sqft} sq.ft` : '-'}
                    {u.bedrooms ? ` · ${u.bedrooms} BHK` : ''}
                  </TableCell>
                  <TableCell>
                    {u.resident_name ? (
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.resident_name}</p>
                        <p className="text-xs text-slate-500">{u.resident_email || u.resident_phone || ''}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">No resident</span>
                    )}
                  </TableCell>
                  <TableCell><Badge className={`text-xs capitalize ${statusBadge(u.status)}`}>{u.status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-slate-500 hover:text-slate-700 h-7 w-7 p-0" data-testid={`edit-unit-${u.unit_id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteUnit(u.unit_id)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-unit-${u.unit_id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Unit Dialog */}
      <Dialog open={showNew} onOpenChange={(open) => { setShowNew(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Outfit']">Add New Unit</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Unit Number *</Label>
                <Input value={unitNumber} onChange={e => setUnitNumber(e.target.value)} placeholder="e.g. A-101" data-testid="unit-number-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Block</Label>
                <Input value={block} onChange={e => setBlock(e.target.value)} placeholder="e.g. A, B" data-testid="unit-block-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Floor</Label>
                <Input value={floor} onChange={e => setFloor(e.target.value)} placeholder="e.g. 1" data-testid="unit-floor-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={unitType} onValueChange={setUnitType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Area (sq.ft)</Label>
                <Input type="number" value={areaSqft} onChange={e => setAreaSqft(e.target.value)} placeholder="1200" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Bedrooms</Label>
                <Input type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} placeholder="2" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacant">Vacant</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="maintenance">Under Maintenance</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Resident Details (optional)</p>
              <div className="space-y-2">
                <Input value={residentName} onChange={e => setResidentName(e.target.value)} placeholder="Resident name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="email" value={residentEmail} onChange={e => setResidentEmail(e.target.value)} placeholder="Email" />
                  <Input value={residentPhone} onChange={e => setResidentPhone(e.target.value)} placeholder="Phone" />
                </div>
              </div>
            </div>
            <Button onClick={createUnit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {submitting && <Loader2 size={14} className="animate-spin mr-2" />} Add Unit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={!!editUnit} onOpenChange={(open) => { if (!open) { setEditUnit(null); resetForm(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit Unit {editUnit?.unit_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Unit Number *</Label>
                <Input value={unitNumber} onChange={e => setUnitNumber(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Block</Label>
                <Input value={block} onChange={e => setBlock(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Floor</Label>
                <Input value={floor} onChange={e => setFloor(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={unitType} onValueChange={setUnitType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Area (sq.ft)</Label>
                <Input type="number" value={areaSqft} onChange={e => setAreaSqft(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Bedrooms</Label>
                <Input type="number" value={bedrooms} onChange={e => setBedrooms(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacant">Vacant</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="maintenance">Under Maintenance</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Resident Details</p>
              <div className="space-y-2">
                <Input value={residentName} onChange={e => setResidentName(e.target.value)} placeholder="Resident name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="email" value={residentEmail} onChange={e => setResidentEmail(e.target.value)} placeholder="Email" />
                  <Input value={residentPhone} onChange={e => setResidentPhone(e.target.value)} placeholder="Phone" />
                </div>
              </div>
            </div>
            <Button onClick={updateUnit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {submitting && <Loader2 size={14} className="animate-spin mr-2" />} Update Unit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog open={showBulk} onOpenChange={(open) => { setShowBulk(open); if (!open) { setBulkPattern(''); setBulkPreview([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Bulk Add Units</DialogTitle>
            <DialogDescription>Add multiple units at once using a range pattern</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs mb-1 block">Unit Numbers Pattern *</Label>
              <Textarea
                value={bulkPattern}
                onChange={e => updateBulkPreview(e.target.value)}
                placeholder="Enter ranges like: 101-104, 201-204, 301-304"
                rows={3}
                data-testid="bulk-pattern-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Examples: "101-110" creates 101, 102...110 | "A101-A104, B101-B104" | "G01-G05, 101-105"
              </p>
            </div>
            
            {bulkPreview.length > 0 && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Preview ({parseBulkPattern(bulkPattern).length} units)</p>
                <div className="flex flex-wrap gap-1">
                  {bulkPreview.map((u, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{bulkBlock ? `${bulkBlock}-${u}` : u}</Badge>
                  ))}
                  {parseBulkPattern(bulkPattern).length > 20 && (
                    <Badge variant="secondary" className="text-xs">+{parseBulkPattern(bulkPattern).length - 20} more</Badge>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Block (optional prefix)</Label>
                <Input value={bulkBlock} onChange={e => setBulkBlock(e.target.value)} placeholder="e.g. A, Tower-1" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={bulkType} onValueChange={setBulkType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Area (sq.ft)</Label>
                <Input type="number" value={bulkArea} onChange={e => setBulkArea(e.target.value)} placeholder="1200" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Bedrooms</Label>
                <Input type="number" value={bulkBedrooms} onChange={e => setBulkBedrooms(e.target.value)} placeholder="2" />
              </div>
            </div>

            <Button 
              onClick={createBulkUnits} 
              disabled={submitting || bulkPreview.length === 0} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-create-btn"
            >
              {submitting && <Loader2 size={14} className="animate-spin mr-2" />}
              Create {parseBulkPattern(bulkPattern).length} Units
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
